/**
 * Calculate scores by mapping prefix for CRF and CCC mappings
 * @param {Array} processedItems - Array of processed safeguard items
 * @param {Array} combinedDataArray - Array of combined data entries
 * @param {string} [targetYear] - Optional year to match CRF-S self-references against (e.g. "2026").
 *                                 When omitted, defaults to the frameworkversion of the first Combined entry.
 *                                 Pass this when comparing a legacy assessment against a different CRFS year.
 * @returns {Object} Object containing crfAverages and cccAverages
 */
function calculateScoresByMappingPrefix(processedItems, combinedDataArray, targetYear) {
	console.log('[calculateScoresByMappingPrefix] START', {
		processedItemsCount: processedItems.length,
		combinedIsArray: Array.isArray(combinedDataArray),
		combinedLength: combinedDataArray ? combinedDataArray.length : 0,
		combinedType: combinedDataArray === null || combinedDataArray === undefined ? 'null/undefined' : typeof combinedDataArray,
		targetYearOverride: targetYear || '(none)'
	});

	const sampleEntry = combinedDataArray && combinedDataArray[0];
	const sourceYear = sampleEntry?.frameworkversion?.S;
	const currentYear = targetYear || sourceYear;
	console.log('[calculateScoresByMappingPrefix] sourceYear:', sourceYear, 'effectiveYear (for CRF-S matching):', currentYear);

	const processedSafeguardInfo = {};
	processedItems.forEach(item => {
		processedSafeguardInfo[item.safeguardid.S] = {
			score: item.maturityscore, // maturityscore is null for Not Applicable items via processUniqueItems
			status: parseInt(item.policystatus.N, 10)
		};
	});
	// console.log('[calculateScoresByMappingPrefix] Processed safeguard info:', processedSafeguardInfo);

	const controlCrfPrefixStats = {}; // Keyed by prefix from crfmapping (e.g., "RES")
	const controlCccPrefixStats = {}; // Keyed by prefix from cccmapping (e.g., "OLD-AM")
	const processedSafeguardToMappedPrefixes = {};
	let debugMatchedRelatedCount = 0;
	let debugCccMappingCount = 0;
	let debugCrfsRelatedSeen = 0;
	let debugWhyNoMatchLogged = 0;

	if (!combinedDataArray || !Array.isArray(combinedDataArray)) {
		console.log('[calculateScoresByMappingPrefix] Combined is not an array, skipping mapping loop', { combinedDataArray: combinedDataArray === null ? 'null' : combinedDataArray === undefined ? 'undefined' : typeof combinedDataArray });
	}
	const isCrfSStandard = (name) => name === 'Cybersecurity Risk Foundation - Safeguards (CRF-S)' || name === 'Cybersecurity Risk Foundation - Safeguards - Core Edition (CRF-S)';

	if (combinedDataArray && Array.isArray(combinedDataArray)) {
		// One-time check for 2025 self-referential mapping
		const has2025mapping = combinedDataArray.some(sourceStandardEntry =>
			sourceStandardEntry.relatedCSItems && sourceStandardEntry.relatedCSItems.some(relatedItem =>
				isCrfSStandard(relatedItem.standardname?.S) &&
				relatedItem.standardversion?.S === '2025'
			)
		);

		let debugFirstRelatedNamesLogged = false;
		combinedDataArray.forEach(sourceStandardEntry => { // e.g., sourceStandardEntry.safeguardid.S is "AM-01" (the original safeguard from the project's standard)
			const originalSafeguardId = sourceStandardEntry.safeguardid.S;

			if (sourceStandardEntry.relatedCSItems && Array.isArray(sourceStandardEntry.relatedCSItems)) {
				if (!debugFirstRelatedNamesLogged && sourceStandardEntry.relatedCSItems.length > 0) {
					debugFirstRelatedNamesLogged = true;
					console.log('[calculateScoresByMappingPrefix] First entry relatedCSItems sample', {
						safeguardid: originalSafeguardId,
						relatedCount: sourceStandardEntry.relatedCSItems.length,
						relatedStandards: sourceStandardEntry.relatedCSItems.map(r => ({ name: r.standardname?.S, version: r.standardversion?.S, controlid: r.controlid?.S, controluid: r.controluid?.S })).slice(0, 5)
					});
				}
				//console.log(`[calculateScoresByMappingPrefix] Checking safeguard: ${originalSafeguardId}. It has ${sourceStandardEntry.relatedCSItems.length} related CS items.`);
				sourceStandardEntry.relatedCSItems.forEach(related => {
					//console.log(`[calculateScoresByMappingPrefix] -> Related item for ${originalSafeguardId}: standard='${related.standardname?.S}', version='${related.standardversion?.S}', controlid='${related.controlid?.S}'`);
				});

				if (!processedSafeguardToMappedPrefixes[originalSafeguardId]) {
					processedSafeguardToMappedPrefixes[originalSafeguardId] = { crf: new Set(), ccc: new Set() };
				}

				// Process CCC mapping from ANY related item (2026 Combined has CCC on CIS/ISO/CCB etc., not on CRF-S self-reference)
				try {
				const cccMappingRaw = (raw) => (raw && (raw.S !== undefined ? raw.S : raw));
				sourceStandardEntry.relatedCSItems.forEach(relatedItem => {
					const cccStr = cccMappingRaw(relatedItem.cccmapping);
					if (cccStr && typeof cccStr === 'string' && cccStr.trim()) {
						debugCccMappingCount++;
						const cccMappingIds = cccStr.split(/\n|\\n/).map(s => s.trim()).filter(Boolean);
						cccMappingIds.forEach(mappingId => {
							const parts = mappingId.split('-');
							if (parts.length > 0) {
								const cccPrefix = parts[0];
								if (!controlCccPrefixStats[cccPrefix]) {
									controlCccPrefixStats[cccPrefix] = {
										totalMaturityScore: 0,
										scoredNonNaSafeguards: new Set(),
										potentialSafeguards: new Set(),
										naMarkedSafeguards: new Set()
									};
								}
								controlCccPrefixStats[cccPrefix].potentialSafeguards.add(originalSafeguardId);
								processedSafeguardToMappedPrefixes[originalSafeguardId].ccc.add(cccPrefix);
								if (processedSafeguardInfo[originalSafeguardId] && processedSafeguardInfo[originalSafeguardId].status === 1) {
									controlCccPrefixStats[cccPrefix].naMarkedSafeguards.add(originalSafeguardId);
								}
							}
						});
					}
				});
				} catch (cccErr) {
					console.error('[calculateScoresByMappingPrefix] Error in CCC processing for safeguard', originalSafeguardId, cccErr);
				}

				sourceStandardEntry.relatedCSItems.forEach(relatedItem => {
					if (relatedItem.standardname && isCrfSStandard(relatedItem.standardname.S)) {
						debugCrfsRelatedSeen++;
						if (debugCrfsRelatedSeen === 1) {
							console.log('[calculateScoresByMappingPrefix] First CRF-S related item sample', {
								originalSafeguardId,
								standardversion: relatedItem.standardversion && relatedItem.standardversion.S,
								standardversionType: typeof (relatedItem.standardversion && relatedItem.standardversion.S),
								controlid: relatedItem.controlid && relatedItem.controlid.S,
								controluid: relatedItem.controluid && relatedItem.controluid.S,
								currentYear,
								currentYearType: typeof currentYear
							});
						}
					// controluid can be "CRF-S-2025-ID-17" (pre-2026) or "CRF-S-CORE-2026-ID-17" / "CRF-S-SMB-2026-PNA-13" (2026 variants)
					const uid = relatedItem.controluid && relatedItem.controluid.S;
					const uidMatchesYear = uid && (uid.startsWith(`CRF-S-${currentYear}-`) || (uid.startsWith('CRF-S-') && uid.includes(`-${currentYear}-`)));
					const versionMatch = relatedItem.standardversion && relatedItem.standardversion.S === currentYear;
					const controlIdMatch = relatedItem.controlid && relatedItem.controlid.S === originalSafeguardId;
					// For same-year self-references, require all three conditions.
					// For cross-version comparisons the controlid will differ
					// (e.g. 2022's WF-08 maps to 2026's PNA-20), so only require version + UID match.
					const isCrossVersion = sourceYear && currentYear && sourceYear !== currentYear;
					const isMatch = versionMatch && uidMatchesYear && (isCrossVersion || controlIdMatch);
					if (debugWhyNoMatchLogged < 3 && versionMatch && !isMatch) {
						debugWhyNoMatchLogged++;
						console.log('[calculateScoresByMappingPrefix] CRF-S related item did not match', {
							originalSafeguardId,
							controlid: relatedItem.controlid && relatedItem.controlid.S,
							controluid: uid,
							currentYear,
							sourceYear,
							versionMatch,
							uidMatchesYear,
							controlIdMatch,
							isCrossVersion,
							reason: !versionMatch ? 'version' : !uidMatchesYear ? 'uid' : 'controlId'
						});
					}
					if (isMatch) {
							debugMatchedRelatedCount++;
							// Process CRF Mapping (e.g., crfmapping.S is "RES-25")
							if (relatedItem.crfmapping && relatedItem.crfmapping.S) {
								//console.log(`[calculateScoresByMappingPrefix] DEBUG: Found CRF mapping for ${originalSafeguardId}:`, relatedItem.crfmapping.S);
								const crfMappingIds = relatedItem.crfmapping.S.split(/\\n|\n/).map(s => s.trim()).filter(s => s);
								crfMappingIds.forEach(mappingId => { // mappingId is "RES-25"
									const parts = mappingId.split('-');
									if (parts.length > 0) {
										const crfPrefix = parts[0]; // Extract "RES" from "RES-25"
										//console.log(`[calculateScoresByMappingPrefix] DEBUG: Processing CRF prefix ${crfPrefix} for safeguard ${originalSafeguardId}`);

										if (!controlCrfPrefixStats[crfPrefix]) {
											controlCrfPrefixStats[crfPrefix] = {
												totalMaturityScore: 0,
												scoredNonNaSafeguards: new Set(),
												potentialSafeguards: new Set(),
												naMarkedSafeguards: new Set()
											};
										}
										controlCrfPrefixStats[crfPrefix].potentialSafeguards.add(originalSafeguardId);
										processedSafeguardToMappedPrefixes[originalSafeguardId].crf.add(crfPrefix);

										if (processedSafeguardInfo[originalSafeguardId] && processedSafeguardInfo[originalSafeguardId].status === 1) {
											controlCrfPrefixStats[crfPrefix].naMarkedSafeguards.add(originalSafeguardId);
										}
									}
								});
							} else {
								// console.log(`[calculateScoresByMappingPrefix] DEBUG: No CRF mapping found for ${originalSafeguardId}`);
							}
						}
						// CCC mapping is processed from any related item in the first loop above
					}
				});
			}
		});

		console.log('[calculateScoresByMappingPrefix] After processing Combined: CRF-S relatedItems seen=' + debugCrfsRelatedSeen + ', matchedRelatedItems=' + debugMatchedRelatedCount + ', cccMappingHits=' + debugCccMappingCount,
			'cccPrefixes=' + Object.keys(controlCccPrefixStats).length,
			Object.keys(controlCccPrefixStats).slice(0, 15).join(', ') + (Object.keys(controlCccPrefixStats).length > 15 ? '...' : ''));
	}


	processedItems.forEach(item => {
		const safeguardId = item.safeguardid.S; // This is the original safeguard ID, e.g., "AM-01"
		const maturityScore = item.maturityscore;
		const policyStatus = parseInt(item.policystatus.N, 10);

		if (policyStatus !== 1 && maturityScore !== null) { // Item is applicable and scored
			if (processedSafeguardToMappedPrefixes[safeguardId]) {
				const mappedInfo = processedSafeguardToMappedPrefixes[safeguardId];

				// CRF Mapping Score Aggregation
				if (mappedInfo.crf) {
					mappedInfo.crf.forEach(crfMappedPrefix => { // crfMappedPrefix is "RES"
						if (controlCrfPrefixStats[crfMappedPrefix]) {
							controlCrfPrefixStats[crfMappedPrefix].totalMaturityScore += maturityScore;
							controlCrfPrefixStats[crfMappedPrefix].scoredNonNaSafeguards.add(safeguardId);
						}
					});
				}

				// CCC Mapping Score Aggregation
				if (mappedInfo.ccc) {
					mappedInfo.ccc.forEach(cccMappedPrefix => {
						if (controlCccPrefixStats[cccMappedPrefix]) {
							controlCccPrefixStats[cccMappedPrefix].totalMaturityScore += maturityScore;
							controlCccPrefixStats[cccMappedPrefix].scoredNonNaSafeguards.add(safeguardId);
						}
					});
				}
			}
		}
	});

	const calculateAveragesForStats = (prefixStats, logLabel) => {
		const finalAverages = {};
		Object.keys(prefixStats).forEach(prefix => {
			const stats = prefixStats[prefix];
			const potentialCount = stats.potentialSafeguards.size;
			const naCount = stats.naMarkedSafeguards.size;
			const scoredCount = stats.scoredNonNaSafeguards.size;
			const totalScoreSum = stats.totalMaturityScore;

			const effectivePotentialItems = potentialCount - naCount;
			let averageOfFilledScaled = 0.00;

			if (effectivePotentialItems > 0) {
				// totalScoreSum includes scores only from answered, non-NA items.
				// Unanswered items correctly contribute 0 to the numerator.
				// The denominator is all items for this prefix, minus those marked "Not Applicable".
				const averageRawScore = totalScoreSum / effectivePotentialItems;
				averageOfFilledScaled = parseFloat((averageRawScore * 5).toFixed(2));
			}

			finalAverages[prefix] = {
				averageScore: averageOfFilledScaled,
					totalPotentialItems: potentialCount,
					notApplicableItems: naCount,
					scoredItems: scoredCount,
					effectivePotentialItems: effectivePotentialItems,
					summedMaturityScoreOfScored: totalScoreSum
			};
		});
		// console.log(`[calculateScoresByMappingPrefix] Final ${logLabel} calculated averages:`, finalAverages);
		return finalAverages;
	};

	const finalCrfAverages = calculateAveragesForStats(controlCrfPrefixStats, 'crfmapping');
	const finalCccAverages = calculateAveragesForStats(controlCccPrefixStats, 'cccmapping');

	console.log('[calculateScoresByMappingPrefix] END cccAverages keys:', Object.keys(finalCccAverages).length, Object.keys(finalCccAverages).slice(0, 20));
	return { crfAverages: finalCrfAverages, cccAverages: finalCccAverages };
}

// Export for use in other modules
if (typeof module !== 'undefined' && module.exports) {
	module.exports = calculateScoresByMappingPrefix;
} else if (typeof window !== 'undefined') {
	window.calculateScoresByMappingPrefix = calculateScoresByMappingPrefix;
}
